// src/pages/Admin.js
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import {
  BUILTIN_CATEGORIES,
  RESERVED_CATEGORY_KEYS,
  mergeCategories,
  slugifyCategoryKey,
  getCategoryLabel,
} from '../config/categories';
import { TEAMS } from '../config/teams';
import {
  isAdminEmail,
  isMeltplanEmail,
  getAdminRecord,
  canManageResource,
  allowedCategoryKeys,
  BOOTSTRAP_ADMIN_EMAILS,
} from '../config/admins';
import { subscribeResources, addResource, updateResource, deleteResource } from '../data/resources';
import { subscribeAdmins, addAdmin, updateAdmin, removeAdmin } from '../data/admins';
import {
  subscribeCategories,
  addCategory,
  updateCategory,
  deleteCategory,
} from '../data/categories';
import { Prose } from './shared';

// Firestore caps a document at 1 MiB. Keep Page Markdown well under that,
// leaving headroom for the other fields.
const MAX_MARKDOWN_BYTES = 900 * 1024;

const Wrap = styled.div`
  max-width: 1120px;
  margin: 0 auto;
  padding: 32px 24px 64px;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 4px 0;
`;

const PageSubtitle = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0 0 24px 0;
`;

const NotAuthorized = styled.div`
  max-width: 480px;
  margin: 80px auto;
  text-align: center;
  color: #64748b;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 40px;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const Button = styled.button`
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 8px;
  border: 1px solid #0e8c6a;
  background: #0e8c6a;
  color: #fff;
  cursor: pointer;

  &:hover {
    background: #0b7057;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled(Button)`
  background: #ffffff;
  color: #0e8c6a;

  &:hover {
    background: #f0fdf9;
  }
`;

const TableWrap = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 32px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

const Th = styled.th`
  text-align: left;
  padding: 10px 16px;
  background: #f8fafc;
  color: #64748b;
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  border-bottom: 1px solid #e2e8f0;
`;

const Td = styled.td`
  padding: 12px 16px;
  border-bottom: 1px solid #e2e8f0;
  color: #1e293b;
  vertical-align: middle;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #0e8c6a;
  background: #e6f5f0;
`;

const RowActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const LinkButton = styled.button`
  border: none;
  background: none;
  padding: 4px 6px;
  font-size: 13px;
  font-weight: 600;
  color: #0e8c6a;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const DeleteLinkButton = styled(LinkButton)`
  color: #dc2626;
`;

const ConfirmText = styled.span`
  font-size: 13px;
  color: #64748b;
`;

const Muted = styled.span`
  color: #94a3b8;
`;

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`;

const FilterLabel = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin-right: 2px;
`;

const Chip = styled.button`
  appearance: none;
  border: 1px solid ${(props) => (props.$active ? '#0e8c6a' : '#e2e8f0')};
  background: ${(props) => (props.$active ? '#e6f5f0' : '#ffffff')};
  color: ${(props) => (props.$active ? '#0e8c6a' : '#475569')};
  padding: 5px 12px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: #0e8c6a;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  max-width: 340px;
  padding: 9px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  color: #1e293b;
  background: #ffffff;

  &:focus {
    outline: none;
    border-color: #0e8c6a;
    box-shadow: 0 0 0 3px rgba(14, 140, 106, 0.12);
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

const AdminTabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 24px;
`;

const AdminTab = styled.button`
  appearance: none;
  border: none;
  background: none;
  padding: 10px 14px;
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => (props.$active ? '#0e8c6a' : '#64748b')};
  border-bottom: 2px solid ${(props) => (props.$active ? '#0e8c6a' : 'transparent')};
  margin-bottom: -1px;
  cursor: pointer;

  &:hover {
    color: ${(props) => (props.$active ? '#0e8c6a' : '#1e293b')};
  }
`;

const TabCount = styled.span`
  display: inline-block;
  margin-left: 6px;
  padding: 1px 7px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  color: ${(props) => (props.$active ? '#0e8c6a' : '#94a3b8')};
  background: ${(props) => (props.$active ? '#e6f5f0' : '#f1f5f9')};
`;

const EmptyState = styled.div`
  color: #64748b;
  font-size: 14px;
  padding: 32px;
  text-align: center;
`;

const FormPanel = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 32px;
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  z-index: 2000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 40px 20px;
  overflow-y: auto;
`;

const ModalCard = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 24px;
  width: 100%;
  max-width: 880px;
  margin: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
`;

const CloseButton = styled.button`
  appearance: none;
  border: none;
  background: none;
  font-size: 22px;
  line-height: 1;
  color: #94a3b8;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 8px;

  &:hover {
    color: #1e293b;
    background: #f1f5f9;
  }
`;

const FormTitle = styled.h2`
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 18px 0;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldFull = styled(Field)`
  grid-column: 1 / -1;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const inputStyles = `
  padding: 9px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  color: #1e293b;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #0e8c6a;
    box-shadow: 0 0 0 3px rgba(14, 140, 106, 0.12);
  }
`;

const Input = styled.input`
  ${inputStyles}
`;

const Select = styled.select`
  ${inputStyles}
`;

const TextArea = styled.textarea`
  ${inputStyles}
  min-height: 320px;
  resize: vertical;
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
  font-size: 13px;
  line-height: 1.5;
`;

const PointersArea = styled.textarea`
  ${inputStyles}
  min-height: 92px;
  resize: vertical;
  line-height: 1.5;
`;

const RadioRow = styled.div`
  display: flex;
  gap: 16px;
  padding-top: 4px;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #1e293b;
  cursor: pointer;
`;


const ScopeText = styled.span`
  font-size: 13px;
  color: #475569;
`;

const HelpText = styled.p`
  font-size: 12px;
  color: #94a3b8;
  margin: 4px 0 0 0;
`;

const MarkdownEditRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const PreviewBox = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  min-height: 320px;
  max-height: 480px;
  overflow-y: auto;
  background: #f8fafc;
`;

const FormActions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  margin-bottom: 16px;
`;

// ── Intuitive selection controls (replace bare radios/checkboxes) ─────────

// Segmented control — used for the admin Role choice.
const SegRow = styled.div`
  display: inline-flex;
  padding: 3px;
  gap: 3px;
  background: #f1f5f9;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
`;

const SegBtn = styled.button`
  appearance: none;
  border: none;
  cursor: pointer;
  padding: 7px 18px;
  border-radius: 7px;
  font-size: 13px;
  font-weight: 600;
  color: ${(props) => (props.$active ? '#0e8c6a' : '#64748b')};
  background: ${(props) => (props.$active ? '#ffffff' : 'transparent')};
  box-shadow: ${(props) => (props.$active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none')};
  transition: all 0.15s ease;

  &:hover {
    color: ${(props) => (props.$active ? '#0e8c6a' : '#1e293b')};
  }
`;

// A wrapping row of selectable pills — used for categories & team limits.
const ChoiceWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding-top: 2px;
`;

const ChoiceChip = styled.button`
  appearance: none;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  cursor: pointer;
  padding: 7px 13px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  border: 1px solid ${(props) => (props.$active ? '#0e8c6a' : '#e2e8f0')};
  background: ${(props) => (props.$active ? '#e6f5f0' : '#ffffff')};
  color: ${(props) => (props.$active ? '#0e8c6a' : '#475569')};
  transition: all 0.15s ease;

  &:hover {
    border-color: #0e8c6a;
    background: ${(props) => (props.$active ? '#dcf0e9' : '#f8fafc')};
  }
`;

// The little check that appears on a selected pill.
const ChipCheck = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 15px;
  height: 15px;
  border-radius: 50%;
  font-size: 10px;
  font-weight: 700;
  color: #ffffff;
  background: #0e8c6a;
`;

const SelectionHint = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #94a3b8;
`;

const emptyForm = {
  category: BUILTIN_CATEGORIES[0].key,
  kind: 'link',
  title: '',
  description: '',
  iconUrl: '',
  url: '',
  markdown: '',
  order: 0,
  team: '',
  userPointers: '',
  officialGuideUrl: '',
};

function resourceToForm(resource) {
  return {
    category: resource.category || BUILTIN_CATEGORIES[0].key,
    kind: resource.kind || 'link',
    title: resource.title || '',
    description: resource.description || '',
    iconUrl: resource.iconUrl || '',
    url: resource.url || '',
    markdown: resource.markdown || '',
    order: typeof resource.order === 'number' ? resource.order : 0,
    team: resource.team || '',
    userPointers: resource.userPointers || '',
    officialGuideUrl: resource.officialGuideUrl || '',
  };
}

function Admin({ user }) {
  const [adminRows, setAdminRows] = useState([]);
  const [adminsLoaded, setAdminsLoaded] = useState(false);

  const [customCats, setCustomCats] = useState([]);

  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // Which admin sub-tab is visible: 'resources' | 'categories' | 'admins'
  const [adminView, setAdminView] = useState('resources');

  // Resources view filters
  const [resSearch, setResSearch] = useState('');
  const [resCategory, setResCategory] = useState('all');

  // Debounced Markdown preview (avoids re-rendering large docs on every keystroke)
  const [previewMarkdown, setPreviewMarkdown] = useState('');

  // Admin-management state
  const [adminForm, setAdminForm] = useState({ email: '', role: 'scoped', categories: [], teams: [] });
  const [adminEditing, setAdminEditing] = useState(false);
  const [showAdminForm, setShowAdminForm] = useState(false);
  const [savingAdmin, setSavingAdmin] = useState(false);
  const [removingAdminEmail, setRemovingAdminEmail] = useState(null);
  const [adminMsg, setAdminMsg] = useState('');

  // Category-management state
  const [newCatLabel, setNewCatLabel] = useState('');
  const [savingCat, setSavingCat] = useState(false);
  const [catMsg, setCatMsg] = useState('');
  const [editingCatKey, setEditingCatKey] = useState(null);
  const [editingCatLabel, setEditingCatLabel] = useState('');
  const [confirmDeleteCatKey, setConfirmDeleteCatKey] = useState(null);
  const [busyCatKey, setBusyCatKey] = useState(null);

  // Runtime admin allowlist (also drives this page's access guard).
  useEffect(() => {
    const unsub = subscribeAdmins((rows) => {
      setAdminRows(rows);
      setAdminsLoaded(true);
    });
    return () => unsub();
  }, []);

  // Custom categories (extra dashboard tabs), managed by super admins here.
  useEffect(() => {
    const unsub = subscribeCategories((rows) => setCustomCats(rows));
    return () => unsub();
  }, []);

  const adminEmails = useMemo(() => adminRows.map((r) => r.email), [adminRows]);
  const admin = isAdminEmail(user?.email, adminEmails);

  // Built-in categories first, then any custom ones (e.g. "HR").
  const allCategories = useMemo(() => mergeCategories(customCats), [customCats]);
  const categoryKeys = useMemo(() => allCategories.map((c) => c.key), [allCategories]);

  // The signed-in admin's own record drives what they're allowed to manage.
  const myRecord = useMemo(() => getAdminRecord(user?.email, adminRows), [user, adminRows]);
  const iAmSuper = myRecord?.role === 'super';
  const myCategoryKeys = useMemo(
    () => allowedCategoryKeys(myRecord, categoryKeys),
    [myRecord, categoryKeys]
  );
  const myCategories = useMemo(
    () => allCategories.filter((c) => myCategoryKeys.includes(c.key)),
    [allCategories, myCategoryKeys]
  );
  // A scoped admin restricted to specific teams may only pick those teams.
  const teamLimited = myRecord?.role === 'scoped' && (myRecord?.teams?.length || 0) > 0;
  const teamChoices = teamLimited ? myRecord.teams : TEAMS;

  useEffect(() => {
    if (!admin) return undefined;
    const unsub = subscribeResources((docs) => {
      setResources(docs);
      setLoading(false);
    });
    return () => unsub();
  }, [admin]);

  const sortedResources = useMemo(() => {
    return [...resources].sort((a, b) => {
      if (a.category !== b.category) return a.category.localeCompare(b.category);
      return (a.order || 0) - (b.order || 0);
    });
  }, [resources]);

  // Scoped admins only see (and can act on) resources within their scope.
  const manageableResources = useMemo(
    () => sortedResources.filter((r) => canManageResource(myRecord, r)),
    [sortedResources, myRecord]
  );

  const filteredResources = useMemo(() => {
    const q = resSearch.trim().toLowerCase();
    return manageableResources.filter((r) => {
      if (resCategory !== 'all' && r.category !== resCategory) return false;
      if (!q) return true;
      const hay = `${r.title || ''} ${getCategoryLabel(r.category, allCategories)}`.toLowerCase();
      return hay.includes(q);
    });
  }, [manageableResources, resSearch, resCategory, allCategories]);

  // Resource counts per category — drives the category tab counts and the
  // "can't delete a category that still has resources" guard.
  const resourceCountByCat = useMemo(() => {
    const c = {};
    for (const r of resources) c[r.category] = (c[r.category] || 0) + 1;
    return c;
  }, [resources]);

  // Close the edit/create modal on Escape.
  useEffect(() => {
    if (!showForm) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeForm();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showForm]);

  // Close the Add/Edit admin modal on Escape.
  useEffect(() => {
    if (!showAdminForm) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') resetAdminForm();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showForm]);

  // Debounce the Markdown preview so typing in a large Page stays responsive.
  useEffect(() => {
    const id = setTimeout(() => setPreviewMarkdown(form.markdown), 250);
    return () => clearTimeout(id);
  }, [form.markdown]);

  const markdownBytes = useMemo(
    () => new TextEncoder().encode(form.markdown || '').length,
    [form.markdown]
  );

  // Bootstrap admins (non-removable) + runtime admins, de-duplicated, each with
  // its resolved role/scope for display and editing.
  const combinedAdmins = useMemo(() => {
    const map = new Map();
    for (const e of BOOTSTRAP_ADMIN_EMAILS) {
      map.set(e, { email: e, bootstrap: true, role: 'super', categories: [], teams: [] });
    }
    for (const r of adminRows) {
      const e = (r.email || '').toLowerCase();
      if (!e || map.has(e)) continue;
      const role = r.role === 'scoped' ? 'scoped' : 'super';
      map.set(e, {
        email: e,
        bootstrap: false,
        role,
        categories: Array.isArray(r.categories) ? r.categories : [],
        teams: Array.isArray(r.teams) ? r.teams : [],
      });
    }
    return Array.from(map.values());
  }, [adminRows]);

  if (!adminsLoaded) {
    return (
      <Wrap>
        <NotAuthorized>Checking access…</NotAuthorized>
      </Wrap>
    );
  }

  if (!admin) {
    return (
      <Wrap>
        <NotAuthorized>
          You are not authorized to view this page.
        </NotAuthorized>
      </Wrap>
    );
  }

  function openCreateForm() {
    setEditingId(null);
    // Default to the caller's first allowed category (and forced team, if any).
    const firstCat = iAmSuper ? BUILTIN_CATEGORIES[0].key : myCategoryKeys[0] || BUILTIN_CATEGORIES[0].key;
    const forcedTeam = teamLimited ? teamChoices[0] || '' : '';
    setForm({ ...emptyForm, category: firstCat, team: forcedTeam });
    setError('');
    setShowForm(true);
  }

  function openEditForm(resource) {
    setEditingId(resource.id);
    setForm(resourceToForm(resource));
    setError('');
    setShowForm(true);
  }

  function closeForm() {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm);
    setError('');
  }

  function updateField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (!form.title.trim()) {
      setError('Title is required.');
      return;
    }
    if (form.kind === 'link' && !form.url.trim()) {
      setError('URL is required for a Link resource.');
      return;
    }
    if (form.kind === 'page' && !form.markdown.trim()) {
      setError('Markdown content is required for a Page resource.');
      return;
    }
    if (form.kind === 'page' && markdownBytes > MAX_MARKDOWN_BYTES) {
      setError(
        `This page is too large (${(markdownBytes / 1024).toFixed(0)} KB). A page must stay ` +
          `under ${Math.round(MAX_MARKDOWN_BYTES / 1024)} KB (Firestore's ~1 MB limit). ` +
          `Trim it, split it into multiple pages, or link out to the full doc instead.`
      );
      return;
    }
    // Scope guard (also enforced by Firestore rules).
    if (!iAmSuper && !myCategoryKeys.includes(form.category)) {
      setError('You can only add resources in your assigned categories.');
      return;
    }
    if (teamLimited && form.category === 'thirdparty' && !myRecord.teams.includes(form.team)) {
      setError('For Third Party Tools you must pick one of your assigned teams.');
      return;
    }

    const payload = {
      category: form.category,
      kind: form.kind,
      title: form.title.trim(),
      description: form.description.trim(),
      iconUrl: form.iconUrl.trim(),
      order: Number(form.order) || 0,
    };

    if (form.kind === 'link') {
      payload.url = form.url.trim();
      payload.markdown = '';
      payload.team = form.team.trim();
      payload.userPointers = form.userPointers.trim();
      payload.officialGuideUrl = form.officialGuideUrl.trim();
    } else {
      payload.markdown = form.markdown;
      payload.url = '';
      payload.team = '';
      payload.userPointers = '';
      payload.officialGuideUrl = '';
    }

    setSaving(true);
    try {
      if (editingId) {
        await updateResource(editingId, payload);
      } else {
        await addResource(payload);
      }
      closeForm();
    } catch (err) {
      setError(err?.message || 'Failed to save resource.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    setDeletingId(id);
    try {
      await deleteResource(id);
      setConfirmDeleteId(null);
    } catch (err) {
      setError(err?.message || 'Failed to delete resource.');
    } finally {
      setDeletingId(null);
    }
  }

  function resetAdminForm() {
    setAdminForm({ email: '', role: 'scoped', categories: [], teams: [] });
    setAdminEditing(false);
    setShowAdminForm(false);
  }

  function openAddAdminForm() {
    setAdminForm({ email: '', role: 'scoped', categories: [], teams: [] });
    setAdminEditing(false);
    setAdminMsg('');
    setShowAdminForm(true);
  }

  function startEditAdmin(a) {
    setAdminForm({
      email: a.email,
      role: a.role,
      categories: a.categories || [],
      teams: a.teams || [],
    });
    setAdminEditing(true);
    setAdminMsg('');
    setShowAdminForm(true);
  }

  function toggleAdminCategory(key) {
    setAdminForm((prev) => ({
      ...prev,
      categories: prev.categories.includes(key)
        ? prev.categories.filter((k) => k !== key)
        : [...prev.categories, key],
    }));
  }

  function toggleAdminTeam(team) {
    setAdminForm((prev) => ({
      ...prev,
      teams: prev.teams.includes(team)
        ? prev.teams.filter((t) => t !== team)
        : [...prev.teams, team],
    }));
  }

  async function handleSaveAdmin(e) {
    e.preventDefault();
    setAdminMsg('');
    const email = adminForm.email.trim().toLowerCase();
    if (!isMeltplanEmail(email)) {
      setAdminMsg('Enter a valid @meltplan.com email address.');
      return;
    }
    if (adminForm.role === 'scoped' && adminForm.categories.length === 0) {
      setAdminMsg('Pick at least one category for a scoped admin.');
      return;
    }
    setSavingAdmin(true);
    try {
      const opts = {
        role: adminForm.role,
        categories: adminForm.categories,
        teams: adminForm.teams,
      };
      if (adminEditing) {
        await updateAdmin(email, opts);
        setAdminMsg(`Updated access for ${email}.`);
      } else {
        await addAdmin(email, user?.email, opts);
        setAdminMsg(`${email} is now an admin.`);
      }
      resetAdminForm();
    } catch (err) {
      setAdminMsg(err?.message || 'Failed to save admin.');
    } finally {
      setSavingAdmin(false);
    }
  }

  async function handleRemoveAdmin(email) {
    setAdminMsg('');
    setRemovingAdminEmail(email);
    try {
      await removeAdmin(email);
    } catch (err) {
      setAdminMsg(err?.message || 'Failed to remove admin.');
    } finally {
      setRemovingAdminEmail(null);
    }
  }

  // ── Category management (super admins only) ────────────────────────────
  async function handleAddCategory(e) {
    e.preventDefault();
    setCatMsg('');
    const label = newCatLabel.trim();
    if (!label) {
      setCatMsg('Enter a category name.');
      return;
    }
    const key = slugifyCategoryKey(label);
    if (!key) {
      setCatMsg('Use at least one letter or number in the name.');
      return;
    }
    if (RESERVED_CATEGORY_KEYS.includes(key)) {
      setCatMsg(`"${label}" clashes with a built-in category. Pick another name.`);
      return;
    }
    if (customCats.some((c) => (c.key || c.id) === key)) {
      setCatMsg(`A category named "${label}" already exists.`);
      return;
    }
    const maxOrder = customCats.reduce(
      (m, c) => Math.max(m, typeof c.order === 'number' ? c.order : 0),
      0
    );
    setSavingCat(true);
    try {
      await addCategory(key, label, maxOrder + 10, user?.email);
      setNewCatLabel('');
      setCatMsg(`Added the "${label}" category.`);
    } catch (err) {
      setCatMsg(err?.message || 'Failed to add category.');
    } finally {
      setSavingCat(false);
    }
  }

  function startEditCategory(cat) {
    setEditingCatKey(cat.key);
    setEditingCatLabel(cat.label);
    setCatMsg('');
  }

  async function handleRenameCategory(key) {
    const label = editingCatLabel.trim();
    if (!label) {
      setCatMsg('Category name cannot be empty.');
      return;
    }
    setBusyCatKey(key);
    try {
      await updateCategory(key, { label });
      setEditingCatKey(null);
      setEditingCatLabel('');
    } catch (err) {
      setCatMsg(err?.message || 'Failed to rename category.');
    } finally {
      setBusyCatKey(null);
    }
  }

  async function handleDeleteCategory(key) {
    setBusyCatKey(key);
    try {
      await deleteCategory(key);
      setConfirmDeleteCatKey(null);
    } catch (err) {
      setCatMsg(err?.message || 'Failed to delete category.');
    } finally {
      setBusyCatKey(null);
    }
  }

  return (
    <Wrap>
      <PageTitle>Admin</PageTitle>
      <PageSubtitle>Manage the Hub's resources and admins.</PageSubtitle>

      <AdminTabs>
        <AdminTab
          $active={adminView === 'resources'}
          onClick={() => setAdminView('resources')}
        >
          Resources
          <TabCount $active={adminView === 'resources'}>{manageableResources.length}</TabCount>
        </AdminTab>
        {iAmSuper && (
          <AdminTab
            $active={adminView === 'categories'}
            onClick={() => setAdminView('categories')}
          >
            Categories
            <TabCount $active={adminView === 'categories'}>{allCategories.length}</TabCount>
          </AdminTab>
        )}
        {iAmSuper && (
          <AdminTab
            $active={adminView === 'admins'}
            onClick={() => setAdminView('admins')}
          >
            Admins
            <TabCount $active={adminView === 'admins'}>{combinedAdmins.length}</TabCount>
          </AdminTab>
        )}
      </AdminTabs>

      {adminView === 'resources' && (
        <>
      {error && !showForm && <ErrorMessage>{error}</ErrorMessage>}

      <TopBar>
        <div />
        {!showForm && <Button onClick={openCreateForm}>+ New Resource</Button>}
      </TopBar>

      {showForm && (
        <Overlay onClick={closeForm}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <FormTitle style={{ margin: 0 }}>{editingId ? 'Edit Resource' : 'New Resource'}</FormTitle>
            <CloseButton type="button" onClick={closeForm} aria-label="Close">×</CloseButton>
          </ModalHeader>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          <form onSubmit={handleSubmit}>
            <FormGrid>
              <Field>
                <Label htmlFor="category">Category</Label>
                <Select
                  id="category"
                  value={form.category}
                  onChange={(e) => updateField('category', e.target.value)}
                >
                  {(iAmSuper ? allCategories : myCategories).map((cat) => (
                    <option key={cat.key} value={cat.key}>
                      {cat.label}
                    </option>
                  ))}
                </Select>
              </Field>

              <Field>
                <Label>Kind</Label>
                <RadioRow>
                  <RadioLabel>
                    <input
                      type="radio"
                      name="kind"
                      value="link"
                      checked={form.kind === 'link'}
                      onChange={() => updateField('kind', 'link')}
                    />
                    Link
                  </RadioLabel>
                  <RadioLabel>
                    <input
                      type="radio"
                      name="kind"
                      value="page"
                      checked={form.kind === 'page'}
                      onChange={() => updateField('kind', 'page')}
                    />
                    Page
                  </RadioLabel>
                </RadioRow>
              </Field>

              <Field>
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  type="text"
                  value={form.title}
                  onChange={(e) => updateField('title', e.target.value)}
                  placeholder="e.g. Sales Playbook"
                />
              </Field>

              <Field>
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  type="text"
                  value={form.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  placeholder="Short subtitle shown on the tile"
                />
              </Field>

              <Field>
                <Label htmlFor="iconUrl">Icon URL</Label>
                <Input
                  id="iconUrl"
                  type="text"
                  value={form.iconUrl}
                  onChange={(e) => updateField('iconUrl', e.target.value)}
                  placeholder="Optional image URL"
                />
              </Field>

              {form.kind === 'link' && (
                <>
                  <FieldFull>
                    <Label htmlFor="url">URL</Label>
                    <Input
                      id="url"
                      type="text"
                      value={form.url}
                      onChange={(e) => updateField('url', e.target.value)}
                      placeholder="https://..."
                    />
                  </FieldFull>

                  <Field>
                    <Label htmlFor="team">Team</Label>
                    <Select
                      id="team"
                      value={form.team}
                      onChange={(e) => updateField('team', e.target.value)}
                    >
                      {!teamLimited && <option value="">— Select team —</option>}
                      {form.team && !teamChoices.includes(form.team) && (
                        <option value={form.team}>{form.team}</option>
                      )}
                      {teamChoices.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </Select>
                    {teamLimited && (
                      <HelpText>You can only assign teams within your access.</HelpText>
                    )}
                  </Field>

                  <Field>
                    <Label htmlFor="officialGuideUrl">Official guide URL</Label>
                    <Input
                      id="officialGuideUrl"
                      type="text"
                      value={form.officialGuideUrl}
                      onChange={(e) => updateField('officialGuideUrl', e.target.value)}
                      placeholder="Link to the tool's how-to / docs"
                    />
                  </Field>

                  <FieldFull>
                    <Label htmlFor="userPointers">Getting-started pointers (one per line)</Label>
                    <PointersArea
                      id="userPointers"
                      value={form.userPointers}
                      onChange={(e) => updateField('userPointers', e.target.value)}
                      placeholder={'Request access from an admin\nEnable 2FA\nStart with the getting-started guide'}
                    />
                  </FieldFull>
                </>
              )}
            </FormGrid>

            {form.kind === 'page' && (
              <Field style={{ marginBottom: 16 }}>
                <Label htmlFor="markdown">
                  Markdown{' '}
                  <Muted
                    style={{
                      textTransform: 'none',
                      letterSpacing: 0,
                      fontWeight: 500,
                      color: markdownBytes > MAX_MARKDOWN_BYTES ? '#dc2626' : '#94a3b8',
                    }}
                  >
                    — {(markdownBytes / 1024).toFixed(1)} KB / {Math.round(MAX_MARKDOWN_BYTES / 1024)} KB
                  </Muted>
                </Label>
                <MarkdownEditRow>
                  <TextArea
                    id="markdown"
                    value={form.markdown}
                    onChange={(e) => updateField('markdown', e.target.value)}
                    placeholder="# Heading&#10;&#10;Write GitHub-flavoured Markdown here..."
                  />
                  <PreviewBox>
                    <Prose>
                      <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]}>
                        {previewMarkdown || '*Live preview will appear here*'}
                      </ReactMarkdown>
                    </Prose>
                  </PreviewBox>
                </MarkdownEditRow>
              </Field>
            )}

            <FormActions>
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Create Resource'}
              </Button>
              <SecondaryButton type="button" onClick={closeForm} disabled={saving}>
                Cancel
              </SecondaryButton>
            </FormActions>
          </form>
          </ModalCard>
        </Overlay>
      )}

      <FilterBar>
        <SearchInput
          type="text"
          placeholder="Search resources by title..."
          value={resSearch}
          onChange={(e) => setResSearch(e.target.value)}
        />
      </FilterBar>
      <FilterBar>
        <FilterLabel>Category</FilterLabel>
        <Chip $active={resCategory === 'all'} onClick={() => setResCategory('all')}>
          All
        </Chip>
        {(iAmSuper ? allCategories : myCategories).map((cat) => (
          <Chip
            key={cat.key}
            $active={resCategory === cat.key}
            onClick={() => setResCategory(cat.key)}
          >
            {cat.label}
          </Chip>
        ))}
      </FilterBar>

      <TableWrap>
        <Table>
          <thead>
            <tr>
              <Th>Title</Th>
              <Th>Category</Th>
              <Th>Kind</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {filteredResources.map((resource) => (
              <tr key={resource.id}>
                <Td>{resource.title}</Td>
                <Td>{getCategoryLabel(resource.category, allCategories)}</Td>
                <Td>
                  <Badge>{resource.kind}</Badge>
                </Td>
                <Td>
                  {confirmDeleteId === resource.id ? (
                    <RowActions>
                      <ConfirmText>Delete this resource?</ConfirmText>
                      <DeleteLinkButton
                        onClick={() => handleDelete(resource.id)}
                        disabled={deletingId === resource.id}
                      >
                        {deletingId === resource.id ? 'Deleting...' : 'Confirm'}
                      </DeleteLinkButton>
                      <LinkButton onClick={() => setConfirmDeleteId(null)}>Cancel</LinkButton>
                    </RowActions>
                  ) : (
                    <RowActions>
                      <LinkButton onClick={() => openEditForm(resource)}>Edit</LinkButton>
                      <DeleteLinkButton onClick={() => setConfirmDeleteId(resource.id)}>
                        Delete
                      </DeleteLinkButton>
                    </RowActions>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
        {!loading && manageableResources.length === 0 && (
          <EmptyState>No resources yet. Create one to get started.</EmptyState>
        )}
        {!loading && manageableResources.length > 0 && filteredResources.length === 0 && (
          <EmptyState>No resources match your filters.</EmptyState>
        )}
        {loading && <EmptyState>Loading resources...</EmptyState>}
      </TableWrap>
        </>
      )}

      {adminView === 'categories' && iAmSuper && (
        <>
      <PageSubtitle>
        Categories are the tabs across the Dashboard. The four built-in ones are
        fixed; add your own (e.g. <strong>HR</strong>) to give a team its own
        tab, then add resources to it and assign a scoped admin under{' '}
        <strong>Admins</strong>.
      </PageSubtitle>

      {catMsg && (
        <ErrorMessage style={{ background: '#f0fdf9', border: '1px solid #bbf7e0', color: '#0e8c6a' }}>
          {catMsg}
        </ErrorMessage>
      )}

      <FormPanel>
        <FormTitle>Add category</FormTitle>
        <form onSubmit={handleAddCategory}>
          <FormGrid>
            <Field>
              <Label htmlFor="newCat">Name</Label>
              <Input
                id="newCat"
                type="text"
                value={newCatLabel}
                onChange={(e) => setNewCatLabel(e.target.value)}
                placeholder="e.g. HR"
                maxLength={40}
              />
              {newCatLabel.trim() && (
                <HelpText>
                  Tab key: <strong>{slugifyCategoryKey(newCatLabel) || '—'}</strong>
                </HelpText>
              )}
            </Field>
          </FormGrid>
          <FormActions>
            <Button type="submit" disabled={savingCat}>
              {savingCat ? 'Adding...' : 'Add category'}
            </Button>
          </FormActions>
        </form>
      </FormPanel>

      <TableWrap>
        <Table>
          <thead>
            <tr>
              <Th>Category</Th>
              <Th>Type</Th>
              <Th>Resources</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {allCategories.map((cat) => {
              const count = resourceCountByCat[cat.key] || 0;
              const editing = editingCatKey === cat.key;
              const busy = busyCatKey === cat.key;
              return (
                <tr key={cat.key}>
                  <Td>
                    {editing ? (
                      <Input
                        type="text"
                        value={editingCatLabel}
                        maxLength={40}
                        autoFocus
                        onChange={(e) => setEditingCatLabel(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleRenameCategory(cat.key);
                          if (e.key === 'Escape') setEditingCatKey(null);
                        }}
                        style={{ maxWidth: 240 }}
                      />
                    ) : (
                      <>
                        {cat.label} <Muted>· {cat.key}</Muted>
                      </>
                    )}
                  </Td>
                  <Td>
                    <Badge
                      style={
                        cat.builtin
                          ? { color: '#64748b', background: '#f1f5f9' }
                          : undefined
                      }
                    >
                      {cat.builtin ? 'Built-in' : 'Custom'}
                    </Badge>
                  </Td>
                  <Td>{count}</Td>
                  <Td>
                    {cat.builtin ? (
                      <Muted>Can't be changed</Muted>
                    ) : editing ? (
                      <RowActions>
                        <LinkButton onClick={() => handleRenameCategory(cat.key)} disabled={busy}>
                          {busy ? 'Saving...' : 'Save'}
                        </LinkButton>
                        <LinkButton onClick={() => setEditingCatKey(null)}>Cancel</LinkButton>
                      </RowActions>
                    ) : confirmDeleteCatKey === cat.key ? (
                      <RowActions>
                        <ConfirmText>Delete this category?</ConfirmText>
                        <DeleteLinkButton onClick={() => handleDeleteCategory(cat.key)} disabled={busy}>
                          {busy ? 'Deleting...' : 'Confirm'}
                        </DeleteLinkButton>
                        <LinkButton onClick={() => setConfirmDeleteCatKey(null)}>Cancel</LinkButton>
                      </RowActions>
                    ) : (
                      <RowActions>
                        <LinkButton onClick={() => startEditCategory(cat)}>Rename</LinkButton>
                        {count > 0 ? (
                          <Muted title="Move or delete its resources first">
                            In use ({count})
                          </Muted>
                        ) : (
                          <DeleteLinkButton onClick={() => setConfirmDeleteCatKey(cat.key)}>
                            Delete
                          </DeleteLinkButton>
                        )}
                      </RowActions>
                    )}
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </TableWrap>
        </>
      )}

      {adminView === 'admins' && iAmSuper && (
        <>
      <PageSubtitle>
        Anyone with an <strong>@meltplan.com</strong> account can view the Hub.
        <strong> Super</strong> admins manage everything and this list;{' '}
        <strong>scoped</strong> admins can only edit resources in the categories
        (and teams) you assign them.
      </PageSubtitle>

      {adminMsg && (
        <ErrorMessage style={{ background: '#f0fdf9', border: '1px solid #bbf7e0', color: '#0e8c6a' }}>
          {adminMsg}
        </ErrorMessage>
      )}

      <TopBar>
        <div />
        <Button onClick={openAddAdminForm}>+ Add admin</Button>
      </TopBar>

      {showAdminForm && (
        <Overlay onClick={resetAdminForm}>
          <ModalCard style={{ maxWidth: 640 }} onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <FormTitle style={{ margin: 0 }}>
                {adminEditing ? `Edit access — ${adminForm.email}` : 'Add admin'}
              </FormTitle>
              <CloseButton type="button" onClick={resetAdminForm} aria-label="Close">
                ×
              </CloseButton>
            </ModalHeader>
            <form onSubmit={handleSaveAdmin}>
              {adminMsg && <ErrorMessage>{adminMsg}</ErrorMessage>}
              {!adminEditing && (
                <Field style={{ marginBottom: 16 }}>
                  <Label htmlFor="newAdmin">Email</Label>
                  <Input
                    id="newAdmin"
                    type="email"
                    value={adminForm.email}
                    onChange={(e) => setAdminForm((prev) => ({ ...prev, email: e.target.value }))}
                    placeholder="name@meltplan.com"
                    autoFocus
                  />
                </Field>
              )}

              <Field style={{ marginBottom: 16 }}>
                <Label>Role</Label>
                <SegRow role="tablist">
                  <SegBtn
                    type="button"
                    $active={adminForm.role === 'scoped'}
                    onClick={() => setAdminForm((prev) => ({ ...prev, role: 'scoped' }))}
                  >
                    Scoped
                  </SegBtn>
                  <SegBtn
                    type="button"
                    $active={adminForm.role === 'super'}
                    onClick={() => setAdminForm((prev) => ({ ...prev, role: 'super' }))}
                  >
                    Super
                  </SegBtn>
                </SegRow>
                <HelpText>
                  {adminForm.role === 'super'
                    ? 'Full access — manages every resource, every category, and this admin list.'
                    : 'Can only add/edit resources in the categories you pick below.'}
                </HelpText>
              </Field>

              {adminForm.role === 'scoped' && (
                <>
                  <Field style={{ marginBottom: 16 }}>
                    <Label>
                      Categories they can manage{' '}
                      {adminForm.categories.length > 0 && (
                        <SelectionHint>· {adminForm.categories.length} selected</SelectionHint>
                      )}
                    </Label>
                    <ChoiceWrap>
                      {allCategories.map((cat) => {
                        const on = adminForm.categories.includes(cat.key);
                        return (
                          <ChoiceChip
                            key={cat.key}
                            type="button"
                            $active={on}
                            aria-pressed={on}
                            onClick={() => toggleAdminCategory(cat.key)}
                          >
                            {on && <ChipCheck>✓</ChipCheck>}
                            {cat.label}
                          </ChoiceChip>
                        );
                      })}
                    </ChoiceWrap>
                  </Field>

                  <Field style={{ marginBottom: 4 }}>
                    <Label>
                      Team limit — optional{' '}
                      {adminForm.teams.length > 0 && (
                        <SelectionHint>· {adminForm.teams.length} selected</SelectionHint>
                      )}
                    </Label>
                    <ChoiceWrap>
                      {TEAMS.map((t) => {
                        const on = adminForm.teams.includes(t);
                        return (
                          <ChoiceChip
                            key={t}
                            type="button"
                            $active={on}
                            aria-pressed={on}
                            onClick={() => toggleAdminTeam(t)}
                          >
                            {on && <ChipCheck>✓</ChipCheck>}
                            {t}
                          </ChoiceChip>
                        );
                      })}
                    </ChoiceWrap>
                    <HelpText>
                      Leave empty for no team restriction. When set, it narrows{' '}
                      <strong>Third Party Tools</strong> to these teams only.
                    </HelpText>
                  </Field>
                </>
              )}

              <FormActions>
                <Button type="submit" disabled={savingAdmin}>
                  {savingAdmin ? 'Saving...' : adminEditing ? 'Save Changes' : 'Add admin'}
                </Button>
                <SecondaryButton type="button" onClick={resetAdminForm} disabled={savingAdmin}>
                  Cancel
                </SecondaryButton>
              </FormActions>
            </form>
          </ModalCard>
        </Overlay>
      )}

      <TableWrap>
        <Table>
          <thead>
            <tr>
              <Th>Email</Th>
              <Th>Role</Th>
              <Th>Scope</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {combinedAdmins.map((a) => {
              const isYou = (user?.email || '').toLowerCase() === a.email;
              const roleLabel = a.bootstrap ? 'Owner' : a.role === 'scoped' ? 'Scoped' : 'Super';
              return (
                <tr key={a.email}>
                  <Td>
                    {a.email}
                    {isYou && <Muted> (you)</Muted>}
                  </Td>
                  <Td>
                    <Badge>{roleLabel}</Badge>
                  </Td>
                  <Td>
                    {a.role === 'super' ? (
                      <ScopeText>All resources</ScopeText>
                    ) : (
                      <ScopeText>
                        {a.categories.length
                          ? a.categories.map((k) => getCategoryLabel(k, allCategories)).join(', ')
                          : '—'}
                        {a.teams.length > 0 && (
                          <Muted> · Teams: {a.teams.join(', ')}</Muted>
                        )}
                      </ScopeText>
                    )}
                  </Td>
                  <Td>
                    {a.bootstrap ? (
                      <Muted>Can't be changed</Muted>
                    ) : (
                      <RowActions>
                        <LinkButton onClick={() => startEditAdmin(a)}>Edit</LinkButton>
                        <DeleteLinkButton
                          onClick={() => handleRemoveAdmin(a.email)}
                          disabled={removingAdminEmail === a.email}
                        >
                          {removingAdminEmail === a.email ? 'Removing...' : 'Remove'}
                        </DeleteLinkButton>
                      </RowActions>
                    )}
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </TableWrap>
        </>
      )}
    </Wrap>
  );
}

export default Admin;
